import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import Tag from "components/atoms/Tag";
import { NhisTransactionStatus } from "types/nhis";
import { ConfirmManager } from "components/organisms/CommonUI";
import NhisProgressDashboard from "./NhisProgressDashboard";
import NhisStepPreview from "./NhisStepPreview";
import { STATUS_CONFIG, CANCELLABLE_STATUSES } from "./nhisStatus";
import { anonymizeName } from "utils/anonymize";

interface NhisRequestStatusItemProps {
  request: NhisTransactionStatus;
  onCancel?: (transactionId: string) => void;
  variant?: "default" | "compact";
  onClick?: () => void;
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;

  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}일 전`;
}

function classifyErrorMessage(message: string): string {
  if (/인증|사용자 정보|정보를 확인/.test(message)) return "환자 본인 인증 오류";
  if (/조회/.test(message)) return "데이터 조회 오류";
  if (/처방전|전달/.test(message)) return "처방전 전달 오류";
  if (/서비스|심평원|건강보험심사평가원|건강보험공단/.test(message)) return "심평원 서비스 오류";
  if (/요청|만료|링크/.test(message)) return "요청 처리 오류";
  return "요청 처리 오류";
}

const ItemContainer = styled.div<{ $dimmed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ $dimmed }) =>
    $dimmed ? COLOR_SYSTEM.get("Gray")?.[20] : COLOR_SYSTEM.get("Blue")?.[10]};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.6 : 1)};
  position: relative;
`;

const IconWrapper = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DetailButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
  background: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${COLOR_SYSTEM.get("Blue")?.[100]};
  }
`;

const MoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const MenuWrapper = styled.div`
  position: absolute;
  top: calc(100% - 4px);
  right: 4px;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 10;
  min-width: 120px;
  overflow: hidden;
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? COLOR_SYSTEM.get("Red")?.[10] : COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const NhisRequestStatusItem: React.FC<NhisRequestStatusItemProps> = ({
  request,
  onCancel,
  variant = "default",
  onClick,
}) => {
  const config = STATUS_CONFIG[request.status];
  const isDimmed =
    request.status === "DELIVERED" ||
    request.status === "EXPIRED" ||
    request.status === "CANCELLED";
  const isCompact = variant === "compact";
  const showActions = !isCompact && CANCELLABLE_STATUSES.includes(request.status);

  // 이 아이템이 상세 팝업을 띄웠을 때 true. DELIVERED 전환 시 자동 닫기용
  const isDetailOpenRef = useRef(false);

  useEffect(() => {
    if (isDetailOpenRef.current && request.status === "DELIVERED") {
      ConfirmManager.hide();
      isDetailOpenRef.current = false;
    }
  }, [request.status]);

  const handleDetailClick = () => {
    isDetailOpenRef.current = true;
    ConfirmManager.show({
      title: "조회 요청 완료",
      body: (
        <NhisProgressDashboard
          status={request.status}
          patientName={request.patientName}
          createdAt={request.createdAt}
          consentUrl={
            request.token ? `${window.location.origin}/nhis/consent/${request.token}` : undefined
          }
          errorMessage={request.errorMessage}
          onClose={() => {
            isDetailOpenRef.current = false;
            ConfirmManager.hide();
          }}
        >
          <NhisStepPreview />
        </NhisProgressDashboard>
      ),
      showBackdrop: true,
      backdropClose: true,
      showCloseButton: true,
    });
  };

  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const iconBgColor =
    request.status === "FAILED"
      ? (COLOR_SYSTEM.get("Red")?.[50] ?? "#fef2f2")
      : request.status === "DELIVERED"
        ? (COLOR_SYSTEM.get("Green")?.[50] ?? "#f0fdf4")
        : request.status === "EXPIRED" || request.status === "CANCELLED"
          ? (COLOR_SYSTEM.get("Gray")?.[50] ?? "#f9fafb")
          : (COLOR_SYSTEM.get("Blue")?.[50] ?? "#eff6ff");

  const iconColor =
    request.status === "FAILED"
      ? (COLOR_SYSTEM.get("Red")?.[400] ?? "#f87171")
      : request.status === "DELIVERED"
        ? (COLOR_SYSTEM.get("Green")?.[500] ?? "#22c55e")
        : request.status === "EXPIRED" || request.status === "CANCELLED"
          ? (COLOR_SYSTEM.get("Gray")?.[400] ?? "#9ca3af")
          : (COLOR_SYSTEM.get("Blue")?.[400] ?? "#60a5fa");

  // DELIVERED 상태: deliveryResult가 있으면 저장 결과 표시
  let deliveryResultText: string | null = null;
  if (request.status === "DELIVERED" && request.deliveryResult) {
    const r = request.deliveryResult;
    if (
      typeof r.total === "number" &&
      typeof r.new === "number" &&
      typeof r.duplicate === "number"
    ) {
      deliveryResultText = `처방전 정보가 저장되었습니다 (전체 ${r.total}개 중 중복 ${r.duplicate}개 제외, ${r.new}개 입력)`;
    }
  }

  const description =
    request.status === "FAILED" && request.errorMessage
      ? `${classifyErrorMessage(request.errorMessage)} : ${request.errorMessage}`
      : (deliveryResultText ?? config.description);

  const anonymizedName = anonymizeName(request.patientName);

  // compact variant용 요약 문구 — 환자 상세 페이지(default variant)의 description과 동일
  let compactDetail: string | null = null;
  if (isCompact) {
    if (request.status === "DELIVERED" && request.deliveryResult) {
      const r = request.deliveryResult;
      if (typeof r.total === "number" && typeof r.new === "number") {
        compactDetail = `신규 ${r.new}건/전체 ${r.total}건`;
      } else {
        compactDetail = config.description;
      }
    } else {
      compactDetail = config.description;
    }
  }

  const timestampStr =
    request.status === "DELIVERED" && request.deliveredAt
      ? formatRelativeTime(request.deliveredAt)
      : formatRelativeTime(request.createdAt);

  return (
    <ItemContainer
      $dimmed={isDimmed}
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      <IconWrapper $color={iconBgColor}>
        <Icon name={config.icon} color={iconColor} size={16} />
      </IconWrapper>
      <ContentArea>
        <TopRow>
          {!isCompact && (
            <Tag size="Small" variant={config.tagVariant} color={config.tagColor}>
              {config.label}
            </Tag>
          )}
          <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]}>
            {anonymizedName} 환자
          </Text>
          <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[400]}>
            {timestampStr}
          </Text>
        </TopRow>
        <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
          {isCompact ? (compactDetail ?? config.description) : description}
        </Text>
      </ContentArea>
      {showActions && (
        <>
          <DetailButton
            onClick={(e) => {
              e.stopPropagation();
              handleDetailClick();
            }}
          >
            <Icon name="Document" size={14} color={COLOR_SYSTEM.get("Blue")?.[500]} />
            <Text textType="Body2" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              상세
            </Text>
          </DetailButton>
          {onCancel && (
            <div ref={menuRef} style={{ position: "relative" }}>
              <MoreButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu((prev) => !prev);
                }}
                title="더보기"
              >
                <Icon name="Dots Vertical" size={16} color={COLOR_SYSTEM.get("Gray")?.[400]} />
              </MoreButton>
              {showMenu && (
                <MenuWrapper>
                  <MenuItem
                    $danger
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onCancel(request.transactionId);
                    }}
                  >
                    <Icon name="X" size={14} color={COLOR_SYSTEM.get("Red")?.[500]} />
                    <Text textType="Body2" color={COLOR_SYSTEM.get("Red")?.[500]}>
                      요청 취소
                    </Text>
                  </MenuItem>
                </MenuWrapper>
              )}
            </div>
          )}
        </>
      )}
    </ItemContainer>
  );
};

export default NhisRequestStatusItem;
